import React, { useState } from 'react';
import { Palette, Box, ChevronRight, Castle, Armchair, Image as ImageIcon, Grid } from 'lucide-react';
import ColorPaletteSystem from './ColorPaletteSystem';
import BlueprintBridge from './BlueprintBridge';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import ImageGalleryOverlay from './ImageGalleryOverlay';
import BuildingTypeSelector from './BuildingTypeSelector';
import InteriorControls from './InteriorControls';

const ArchitectureAssetPanel: React.FC = () => {
    const { isRightPanelOpen, toggleRightPanel, mode, setMode, gallery } = useArchitectureStore();
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [initialGalleryIndex, setInitialGalleryIndex] = useState(0);

    const openGallery = (index: number) => {
        setInitialGalleryIndex(index);
        setIsGalleryOpen(true);
    };

    return (
        <>
            {/* Toggle Button (Visible when closed) */}
            {!isRightPanelOpen && (
                <button
                    onClick={toggleRightPanel}
                    className="absolute top-4 right-4 z-50 p-3 bg-orange-600 hover:bg-orange-500 text-white rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
                    title="Open Asset Panel"
                >
                    <Box size={20} />
                </button>
            )}

            {/* Panel Content (Collapsible) */}
            <div className={`flex-1 flex flex-col p-4 lg:p-3 gap-6 lg:gap-4 overflow-y-auto transition-all duration-300 ${isRightPanelOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none hidden'}`}>

                {/* Close Button */}
                <button
                    onClick={toggleRightPanel}
                    className="self-end text-stone-500 hover:text-stone-300 p-2"
                >
                    <ChevronRight size={20} />
                </button>

                {/* Mode Switcher (Moved from Bottom Bar) */}
                <div className="flex p-1 bg-stone-900 border border-stone-800 rounded-xl">
                    <button
                        onClick={() => setMode('elevation')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all text-xs font-medium ${mode === 'elevation'
                            ? 'bg-stone-800 text-white shadow-sm'
                            : 'text-stone-500 hover:text-stone-300'
                            }`}
                    >
                        <Castle size={14} />
                        <span>Elevation</span>
                    </button>
                    <button
                        onClick={() => setMode('interior')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all text-xs font-medium ${mode === 'interior'
                            ? 'bg-stone-800 text-white shadow-sm'
                            : 'text-stone-500 hover:text-stone-300'
                            }`}
                    >
                        <Armchair size={14} />
                        <span>Interior</span>
                    </button>
                </div>

                {/* Conditional Rendering Based on Mode */}
                {mode === 'elevation' ? (
                    <>
                        {/* Building Type Selector (New) */}
                        <BuildingTypeSelector />

                        {/* Blueprint Bridge (Moved from Left Panel) */}
                        <div className="mb-4">
                            <BlueprintBridge />
                        </div>
                        {/* Palette Section */}
                        <ColorPaletteSystem />
                    </>
                ) : (
                    <InteriorControls />
                )}

                <div className="h-px bg-stone-800" />

                {/* Assets Section */}
                <div className="flex flex-col gap-3 opacity-50 pointer-events-none filter grayscale">
                    <div className="flex items-center gap-2 text-stone-500 px-1">
                        <Box size={14} />
                        <span className="text-xs font-bold uppercase tracking-widest">Assets</span>
                    </div>
                    <div className="p-10 rounded-xl bg-stone-900/30 border border-stone-800 border-dashed flex items-center justify-center text-stone-600 text-xs text-center">
                        Asset Browser<br />Coming Soon
                    </div>
                </div>

                <div className="h-px bg-stone-800" />

                {/* Gallery Section (New Addon) */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-2 text-stone-500">
                            <ImageIcon size={14} />
                            <span className="text-xs font-bold uppercase tracking-widest">History</span>
                        </div>
                        {gallery.length > 0 && (
                            <span className="text-[10px] bg-stone-800 text-stone-400 px-2 py-0.5 rounded-full">
                                {gallery.length}
                            </span>
                        )}
                    </div>

                    {gallery.length === 0 ? (
                        <div className="p-8 rounded-xl bg-stone-900/30 border border-stone-800 border-dashed flex flex-col items-center justify-center text-stone-600 text-xs text-center gap-2">
                            <Grid size={24} className="opacity-20" />
                            <span>No generations yet.<br />Start creating to populate history.</span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2">
                            {gallery.slice(0, 6).map((img, idx) => (
                                <div
                                    key={idx}
                                    className="relative aspect-square rounded-lg overflow-hidden border border-stone-800 group cursor-pointer hover:border-orange-500/50 transition-colors"
                                    onClick={() => openGallery(idx)}
                                >
                                    <img src={img} alt={`Gen ${idx}`} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                </div>
                            ))}
                            {gallery.length > 6 && (
                                <button
                                    className="aspect-square rounded-lg border border-stone-800 bg-stone-900 flex items-center justify-center text-stone-500 hover:text-white hover:bg-stone-800 transition-colors text-xs font-medium"
                                    onClick={() => openGallery(6)}
                                >
                                    +{gallery.length - 6} more
                                </button>
                            )}
                        </div>
                    )}
                </div>

            </div>

            {/* Gallery Overlay */}
            <ImageGalleryOverlay
                isOpen={isGalleryOpen}
                onClose={() => setIsGalleryOpen(false)}
                images={gallery.map((url, i) => ({ url, name: `Generation ${i + 1}` }))}
                initialIndex={initialGalleryIndex}
            />
        </>
    );
};

export default ArchitectureAssetPanel;
